import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, CircleCheck, CircleX, Clock3, IdCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTILO_ESTADO = {
    pendente: "text-[#c9a24b] border-[#c9a24b]/50",
    aprovado: "text-[#9ec49a] border-[#9ec49a]/40",
    recusado: "text-[#e0a0a0] border-[#e0a0a0]/40",
};
const ROTULO_ESTADO = { pendente: "Pendente", aprovado: "Aprovado", recusado: "Recusado" };

export const AdminAdesoes = ({ token }) => {
    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState(null);
    const headers = { Authorization: `Bearer ${token}` };

    const carregar = useCallback(() => {
        axios.get(`${API}/adesoes`, { headers })
            .then(({ data }) => setDados(data))
            .catch(() => setErro("Não foi possível carregar os pedidos."));
    }, [token]);

    useEffect(() => { carregar(); }, [carregar]);

    const mudarEstado = async (id, estado) => {
        await axios.patch(`${API}/adesoes/${id}`, { estado }, { headers });
        carregar();
    };

    const exportar = async () => {
        try {
            const r = await axios.get(`${API}/adesoes/export`, { headers, responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "adesoes-fados-a-mesa.xlsx";
            a.click();
            URL.revokeObjectURL(url);
        } catch { /* sem permissão ou rede em baixo */ }
    };

    const descarregar = async (p) => {
        try {
            const r = await axios.get(`${API}/adesoes/${p.id}/comprovativo`, { headers, responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `comprovativo-${p.nome.toLowerCase().replace(/\s+/g, "-")}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { /* sem ficheiro */ }
    };

    if (erro) return <p className="font-display italic text-[#e0a0a0] mt-12" data-testid="adesoes-erro">{erro}</p>;
    if (!dados) return <p className="font-display italic text-[#efe6d6]/60 mt-12" data-testid="adesoes-loading">A carregar pedidos...</p>;

    return (
        <div className="mt-10" data-testid="admin-adesoes">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="font-light text-sm text-[#efe6d6]/50" data-testid="adesoes-total">
                    {dados.total === 0 ? "Sem pedidos de adesão por agora." : `${dados.total} pedido(s) de adesão.`}
                </p>
                <button onClick={exportar} disabled={dados.total === 0} data-testid="adesoes-exportar-excel"
                    className="inline-flex items-center gap-2 font-caps text-[10px] tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-5 py-2.5 hover:bg-[#ecd49b] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed">
                    <Download size={14} /> Exportar Excel
                </button>
            </div>
            {dados.total > 0 && (
                <div className="border border-[#c9a24b]/25 overflow-x-auto mt-5">
                    <table className="w-full text-left min-w-[860px]">
                        <thead>
                            <tr className="border-b border-[#c9a24b]/25 bg-[#0a0705]">
                                {["Nome", "Categoria", "Morada", "Contacto", "Data", "Comprovativo", "Estado", "Ações"].map((h) => (
                                    <th key={h} className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-4 py-3.5 whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {dados.pedidos.map((p) => (
                                <tr key={p.id} className="border-b border-[#c9a24b]/10" data-testid={`adesao-${p.id.slice(0, 8)}`}>
                                    <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/90 whitespace-nowrap">{p.nome}</td>
                                    <td className="px-4 py-3 font-light text-sm text-[#efe6d6]/60">{p.categoria === "efetivo" ? "Efetivo" : "Apoiante"}</td>
                                    <td className="px-4 py-3 font-light text-xs text-[#efe6d6]/60">
                                        {p.rua ? `${p.rua}, ${p.cp} ${p.localidade}, ${p.pais}` : "—"}
                                    </td>
                                    <td className="px-4 py-3 font-light text-xs text-[#efe6d6]/60">
                                        {p.email || "—"}{p.telefone ? <><br />{p.telefone}</> : null}
                                    </td>
                                    <td className="px-4 py-3 font-light text-xs text-[#efe6d6]/60 whitespace-nowrap">
                                        {new Date(p.created_at).toLocaleDateString("pt-PT")}
                                    </td>
                                    <td className="px-4 py-3">
                                        {p.tem_comprovativo ? (
                                            <button onClick={() => descarregar(p)} data-testid={`adesao-comprovativo-${p.id.slice(0, 8)}`}
                                                className="inline-flex items-center gap-1.5 font-caps text-[9px] tracking-[0.2em] uppercase text-[#c9a24b] border border-[#c9a24b]/40 px-3 py-1.5 hover:bg-[#c9a24b]/10 transition-colors duration-300">
                                                <Download size={11} /> Ver
                                            </button>
                                        ) : (
                                            <span className="font-light text-xs text-[#efe6d6]/35">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center gap-1.5 font-caps text-[9px] tracking-[0.2em] uppercase border px-3 py-1.5 ${ESTILO_ESTADO[p.estado]}`}
                                            data-testid={`adesao-estado-${p.id.slice(0, 8)}`}>
                                            {p.estado === "pendente" && <Clock3 size={11} />}
                                            {p.estado === "aprovado" && <CircleCheck size={11} />}
                                            {p.estado === "recusado" && <CircleX size={11} />}
                                            {ROTULO_ESTADO[p.estado]}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        {p.estado === "pendente" && (
                                            <div className="flex gap-2">
                                                <button onClick={() => mudarEstado(p.id, "aprovado")} data-testid={`adesao-aprovar-${p.id.slice(0, 8)}`}
                                                    className="font-caps text-[9px] tracking-[0.2em] uppercase text-[#070503] bg-[#c9a24b] px-3 py-1.5 hover:bg-[#ecd49b] transition-colors duration-300">
                                                    Aprovar
                                                </button>
                                                <button onClick={() => mudarEstado(p.id, "recusado")} data-testid={`adesao-recusar-${p.id.slice(0, 8)}`}
                                                    className="font-caps text-[9px] tracking-[0.2em] uppercase text-[#e0a0a0] border border-[#e0a0a0]/40 px-3 py-1.5 hover:bg-[#e0a0a0]/10 transition-colors duration-300">
                                                    Recusar
                                                </button>
                                            </div>
                                        )}
                                        {p.estado === "aprovado" && (
                                            <a href={`/cartao-membro/${p.id}`} target="_blank" rel="noopener noreferrer"
                                                data-testid={`adesao-cartao-${p.id.slice(0, 8)}`}
                                                className="inline-flex items-center gap-1.5 font-caps text-[9px] tracking-[0.2em] uppercase text-[#c9a24b] border border-[#c9a24b]/40 px-3 py-1.5 hover:bg-[#c9a24b]/10 transition-colors duration-300">
                                                <IdCard size={11} /> Cartão
                                            </a>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="font-light text-[11px] text-[#efe6d6]/40 mt-4 max-w-2xl" data-testid="adesoes-nota">
                Nota nDSG: ao recusar um pedido, o comprovativo e os dados pessoais (morada, contacto) são apagados de imediato; fica apenas o nome, a data e a decisão.
            </p>
        </div>
    );
};
